import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/prestashop-zu-shopify";
const TITLE = "PrestaShop zu Shopify wechseln: Ablauf, Kosten, Datenübernahme";
const DESCRIPTION =
  "Von PrestaShop 1.6 oder 1.7 zu Shopify umziehen: Was sich übernehmen lässt, welche Module Aufwand machen, wie der Weiterleitungsplan aussieht und was der Wechsel kostet.";
const UPDATED = "2026-09-16";

const ANSWER =
  "Ein Wechsel von PrestaShop zu Shopify läuft über vier Stufen: Export von Produkten, Kunden und Bestellungen aus der PrestaShop-Datenbank, Nachbau der Kombinationen und Merkmale in Shopify-Varianten und Metafeldern, ein vollständiger 301-Plan für alle alten Adressen und ein gestaffelter Livegang. Üblich sind vier bis acht Wochen, je nach Zahl der eingesetzten Module.";

const faqs = [
  {
    q: "Lassen sich PrestaShop-Daten nach Shopify übernehmen?",
    a: "Produkte mit Kombinationen, Bilder, Kunden und Bestellhistorie lassen sich per CSV-Export oder direkt über die Datenbank übertragen. Merkmale und Attribute muss man vorher sortieren, weil PrestaShop beides getrennt führt und Shopify nur Varianten und Metafelder kennt.",
  },
  {
    q: "Was ist bei PrestaShop 1.6 anders als bei 1.7?",
    a: "1.6 hat meist mehr gewachsene Template-Anpassungen und ältere Module, 1.7 exportiert sauberer, hat aber häufiger eigene Themes auf Basis von Symfony. In beiden Fällen ist nicht die Produktzahl der Kostentreiber, sondern die Zahl der Module, deren Funktion in Shopify neu abgebildet werden muss.",
  },
  {
    q: "Was passiert mit meinen Rankings?",
    a: "Rankings bleiben erhalten, wenn jede alte PrestaShop-Adresse per 301 ein passendes Ziel bekommt. Kritisch sind die ID-basierten Produktadressen, Kategorie-Adressen mit Filterparametern und die CMS-Seiten, die in PrestaShop oft eigene Sichtbarkeit haben.",
  },
  {
    q: "Was kostet der Wechsel von PrestaShop zu Shopify?",
    a: "Kleinere Sortimente ohne Schnittstellen liegen meist bei 5.000 bis 9.000 € netto. Mit Warenwirtschaft, B2B-Preislogik oder mehreren Sprachversionen wird es mehr. Entwicklung berechne ich mit 110 € netto pro Stunde, Conversion-Optimierung ab 3.990 € netto.",
  },
  {
    q: "Wie lange dauert die Migration?",
    a: "Vier bis acht Wochen sind üblich. Den Zeitplan bestimmen die Module, die Sprachversionen und die Zahl der alten Adressen mit Rankings, nicht die Zahl der Produkte.",
  },
];

export const Route = createFileRoute("/ratgeber/prestashop-zu-shopify")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Migration",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Migration"
      title="PrestaShop zu Shopify: der Umzug ohne Sichtbarkeitsverlust"
      intro="PrestaShop-Shops sind selten kaputt, sie sind meist zugewachsen: ein Dutzend Module, ein angepasstes Theme und niemand, der die Updates noch gefahrlos einspielen will. Wer zu Shopify wechselt, tauscht diese Pflege gegen Betriebsruhe und sollte vorher wissen, welche Module im Projekt Arbeit machen."
      source="ratgeber-prestashop-zu-shopify"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Warum Händler von PrestaShop wechseln",
          p: [
            "Der häufigste Grund ist der Betriebsaufwand. Jedes Update betrifft Kern, Theme und Module gleichzeitig, und mit den Jahren traut sich niemand mehr, es einzuspielen. Ein Shop, der nicht aktualisiert wird, wird irgendwann zum Sicherheitsthema.",
            "Shopify nimmt diesen Teil ab. Der Preis dafür ist weniger Freiheit im Checkout und im Datenmodell. Bei Standardsortimenten fällt das kaum auf, bei sehr spezieller Logik gehört es vorher geprüft.",
          ],
        },
        {
          h: "Was sich sauber übernehmen lässt",
          p: [],
          list: [
            "Produkte mit Kombinationen, Bildern, Preisen und Beständen",
            "Kundenkonten, Passwörter werden neu vergeben",
            "Bestellhistorie als Referenzdaten",
            "CMS-Seiten und Blogbeiträge",
          ],
        },
        {
          h: "Was neu gedacht werden muss",
          p: [
            "PrestaShop trennt Attribute und Merkmale. Shopify kennt diese Trennung nicht, deshalb entscheidet sich früh im Projekt, was Variante wird und was Metafeld. Diese Struktur bestimmt später Filterbarkeit und Sichtbarkeit.",
          ],
          list: [
            "Attribute zu Varianten machen, Merkmale zu Metafeldern",
            "Modul-Funktionen prüfen und durch Apps oder Theme-Anpassung ersetzen",
            "Mehrsprachigkeit und Ländershops auf Shopify Markets umstellen",
            "Kundengruppen und Nettopreise für B2B neu aufsetzen",
          ],
        },
        {
          h: "Der Weiterleitungsplan",
          p: [
            "Hier entscheidet sich, ob Sichtbarkeit erhalten bleibt. Ziehen Sie alle Adressen aus PrestaShop und aus der Search Console, sortiert nach Sichtbarkeit und Umsatz, und geben Sie jeder einzelnen ein Ziel.",
          ],
          list: [
            "Produkt-, Kategorie- und CMS-Adressen einzeln weiterleiten",
            "ID-basierte Adressen aus älteren Installationen mitnehmen",
            "Sprachpräfixe wie /de/ und /fr/ sauber zuordnen",
            "Weiterleitungen vor dem Livegang testen",
          ],
        },
        {
          h: "Kostenrahmen",
          p: [
            "Kleinere Sortimente ohne Schnittstellen liegen meist bei 5.000 bis 9.000 € netto, mit Warenwirtschaft und mehreren Sprachversionen darüber. Entwicklung rechne ich mit 110 € netto pro Stunde ab, Conversion-Optimierung ab 3.990 € netto. Nach Sichtung des Shops gibt es ein festes Angebot.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-migration", label: "Shopify Migration im Überblick" },
        { to: "/ratgeber/shopware-zu-shopify", label: "Shopware zu Shopify" },
        { to: "/blog/woocommerce-zu-shopify-migration", label: "WooCommerce zu Shopify" },
        { to: "/kontakt", label: "Migration anfragen" },
      ]}
    />
  );
}
